import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Stack,
} from "@mui/material";
import dayjs from "dayjs";
import { useContext, useMemo, useState } from "react";
import { NotificationTableRow_notification$data } from "./__generated__/NotificationTableRow_notification.graphql";
import CurrentUserContext from "../../../../contexts/CurrentUserContext";
import useUpdateNotification from "../../../../mutations/useUpdateNotification";
import nullthrows from "../../../../utils/nullthrows";
import useNavigateToEditNotification from "./hooks/useNavigateToEditNotification";

type Props = {
  notification: NotificationTableRow_notification$data;
};

const NotificationPublishButton = ({ notification }: Props) => {
  const currentUserContext = useContext(CurrentUserContext);
  const activeBrandID = nullthrows(currentUserContext?.activeBrandID);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [updateNotification, isUpdating] = useUpdateNotification({
    brandID: activeBrandID,
  });
  const navigateToEditNotification = useNavigateToEditNotification();

  const [publishTimestamp, willPublishImmediately] = useMemo(() => {
    let publishTimestamp = dayjs(notification.timestamp);
    let immediate = false;
    if (
      publishTimestamp.isBefore(dayjs()) ||
      notification.confirmationStatus === "confirmed"
    ) {
      publishTimestamp = dayjs();
      immediate = true;
    }
    return [publishTimestamp, immediate];
  }, [notification.timestamp, notification.confirmationStatus]);

  const onDialogClose = () => setIsDialogOpen(false);
  const onConfirmClick = () => {
    updateNotification({
      input: {
        id: notification.id,
        timestamp: publishTimestamp,
      },
      onCompleted: (data, errors) => {
        if (errors) {
          console.log("Error: ", errors);
        } else {
          onDialogClose();
        }
      },
    });
  };
  const onEditClick = () => {
    onDialogClose();
    navigateToEditNotification(notification);
  };

  return (
    <>
      <Button
        size="small"
        variant="text"
        color="secondary"
        onClick={() => setIsDialogOpen(true)}
      >
        Publish
      </Button>
      <Dialog open={isDialogOpen} onClose={onDialogClose}>
        <DialogTitle>Ready to publish this notification?</DialogTitle>
        <DialogContent>
          <DialogContentText>
            This notification will be sent{" "}
            {willPublishImmediately ? (
              <b>immediately to all your customers</b>
            ) : (
              <b>at {publishTimestamp.format("llll z")}</b>
            )}
            .
            <br />
            <br />
            If you’d like to change the audience or schedule a time, click{" "}
            <b>Edit</b> first.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Stack
            direction="row"
            spacing={1}
            justifyContent="space-between"
            width="100%"
            px={2}
          >
            <Button
              disabled={isUpdating}
              onClick={onDialogClose}
              color="primary"
            >
              Cancel
            </Button>
            <Stack direction="row" spacing={2}>
              <Button
                onClick={onEditClick}
                color="secondary"
                variant="outlined"
              >
                Edit
              </Button>
              <Button
                disabled={isUpdating}
                onClick={onConfirmClick}
                variant="contained"
                color="secondary"
              >
                {willPublishImmediately ? "Publish Now" : "Publish"}
              </Button>
            </Stack>
          </Stack>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default NotificationPublishButton;
